/**
 * Builders for XLSX templates — used by ImportDialog "Télécharger le template".
 * Lazy-loaded `xlsx` to keep bundle slim.
 */

export interface TemplateSpec {
  filename: string;
  sheetName: string;
  headers: string[];
  example: (string | number | null)[];
  /** Optional cell A1 note (top instructions). Rendered as the first row before headers. */
  note?: string;
  /**
   * Sprint 6.9.1.1 — colonnes "signature" qu'on cherche pour auto-détecter la VRAIE
   * ligne d'en-tête dans des fichiers Excel KOST avec ligne 1 décorative (titre fusionné).
   * Si ≥ 50 % des `requiredHeaders` se trouvent sur une ligne, on la considère comme la ligne d'en-tête.
   * Laisser undefined = comportement legacy (ligne 1 = headers).
   */
  requiredHeaders?: string[];
}

/**
 * Sprint 6.9.1.1 — Auto-détection de la ligne d'en-tête réelle.
 *
 * Cas 1 — header en ligne 1 (template propre) → retourne 0.
 * Cas 2 — fichier KOST avec titre fusionné "KOST ACADEMY..." en ligne 1
 *         + en-têtes en ligne 2 → retourne 1.
 * Cas 3 — aucune ligne ne match (fichier corrompu / format inconnu) → retourne -1.
 */
export function detectHeaderRow(
  rawRows: unknown[][],
  requiredHeaders: string[],
  maxRowsToScan = 10,
): number {
  const targets = requiredHeaders.map((h) => h.toLowerCase().trim());
  const need = Math.ceil(targets.length * 0.5);
  const scan = Math.min(rawRows.length, maxRowsToScan);
  for (let i = 0; i < scan; i++) {
    const row = (rawRows[i] ?? []).map((c) =>
      String(c ?? '')
        .trim()
        .toLowerCase(),
    );
    let matches = 0;
    for (const target of targets) {
      if (row.some((cell) => cell.includes(target))) matches++;
    }
    if (matches >= need) return i;
  }
  return -1;
}

/** Generate a single-sheet XLSX blob with headers + 1 example row. */
export async function buildTemplate(spec: TemplateSpec): Promise<Blob> {
  const xlsx = await import('xlsx');
  const wb = xlsx.utils.book_new();
  const aoa: (string | number | null)[][] = [];
  if (spec.note) aoa.push([spec.note]);
  aoa.push(spec.headers);
  aoa.push(spec.example);
  const ws = xlsx.utils.aoa_to_sheet(aoa);
  ws['!cols'] = spec.headers.map((h) => ({ wch: Math.max(14, h.length + 2) }));
  xlsx.utils.book_append_sheet(wb, ws, spec.sheetName.slice(0, 31));
  const buf = xlsx.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  return new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export const TEMPLATE_FORMATEURS: TemplateSpec = {
  filename: 'template_formateurs.xlsx',
  sheetName: 'Formateurs',
  headers: [
    'Nom complet',
    'Email',
    'Téléphone',
    'Spécialités',
    'Tarif horaire DZD',
    'Tarif journée DZD',
    'Statut',
    'Bio',
    'Coordonnées (RIB/IBAN)',
    'Remarques internes',
  ],
  example: [
    'Mohamed Saadi',
    'm.saadi@kost.dz',
    '0555 12 34 56',
    'B2B-FIN, B2B-MGMT',
    '8000',
    '60000',
    'externe',
    'Expert finance et management 12 ans',
    'BNA — DZ58 0000 0000 0000 0000 0000 12',
    'Préfère sessions en présentiel',
  ],
  note: 'Statut ∈ interne / externe / vacataire. Spécialités séparées par virgule (codes sous-segments).',
};

export const TEMPLATE_TIERS_CLIENTS: TemplateSpec = {
  filename: 'template_tiers_clients.xlsx',
  sheetName: 'Clients B2B',
  headers: [
    'Entreprise',
    'Email',
    'Téléphone',
    'Adresse',
    'Wilaya',
    'RC',
    'NIF',
    'NIS',
    'Type tier',
    'Activité',
    'Sous-segment',
    'Commercial assigné',
    'Observation',
  ],
  example: [
    'Sonatrach SPA',
    'contact@sonatrach.dz',
    '021 54 32 10',
    'Djenane El Malik, Hydra',
    'Alger',
    '16/00-1234567 B 24',
    '000216123456789',
    '0000216012345678',
    'client_b2b',
    'Kost Academy',
    'B2B-FIN',
    'commercial@kost.dz',
    'Compte stratégique',
  ],
  note: 'Type tier ∈ client_b2b / client_b2c / fournisseur / formateur. Activité = nom exact (ex. Kost Academy).',
};

export const TEMPLATE_FOURNISSEURS: TemplateSpec = {
  filename: 'template_fournisseurs.xlsx',
  sheetName: 'Fournisseurs',
  headers: [
    'Fournisseur',
    'Téléphone',
    'N° compte (RIB/IBAN)',
    'Email',
    'Adresse',
    'Catégorie',
    'Observation',
  ],
  example: [
    'Hôtel El Aurassi',
    '021 74 82 52',
    'CPA — DZ59 0000 0000 0000 0000 0000 99',
    'reservation@elaurassi.dz',
    '2, Bd Frantz Fanon, Alger',
    'Hôtel',
    'Tarif corporate négocié',
  ],
  note: 'Catégorie suggérée : Hôtel / Traiteur / Agence voyages / IT / Autre.',
};

export const TEMPLATE_FORMATIONS: TemplateSpec = {
  filename: 'template_formations.xlsx',
  sheetName: 'Catalogue',
  headers: [
    'Code',
    'Titre',
    'Sous-segment',
    'Description courte',
    'Objectifs',
    'Prérequis',
    'Durée (heures)',
    'Durée (jours)',
    'Niveau',
    'Modalité',
    'Prix B2B inter (DZD)',
    'Prix B2B intra (DZD)',
    'Prix B2C (DZD)',
    'Certif délivrée',
    'Code certif',
    'Validité (mois)',
    'Formateur principal (email)',
    'Statut',
  ],
  example: [
    'FIN-001',
    'Finance pour non-financiers',
    'B2B-FIN',
    "Découvrir les bases de la finance d'entreprise",
    'Lire un bilan, comprendre un compte de résultat',
    'Aucun',
    14,
    2,
    'debutant',
    'presentiel',
    45000,
    320000,
    35000,
    'oui',
    'KOST-FIN-001',
    24,
    'm.saadi@kost.dz',
    'actif',
  ],
  note: 'Niveau ∈ debutant/intermediaire/avance/expert · Modalité ∈ presentiel/distanciel/blended · Statut ∈ actif/draft/archive.',
};

export const TEMPLATE_INSCRIPTIONS: TemplateSpec = {
  filename: 'template_inscriptions.xlsx',
  sheetName: 'Inscriptions',
  headers: [
    'Année',
    'Réf facture',
    'Formation (titre)',
    'Date formation',
    'Lieu',
    'Entreprise (client)',
    'Nb participants',
    'HT (DA)',
    'TVA (DA)',
    'TTC (DA)',
    'Statut',
    'Date règlement',
    'Mode paiement',
    'Référence virement',
    'Commercial (email)',
    'Observation',
  ],
  example: [
    2026,
    'FAC-2026-0042',
    'Finance pour non-financiers',
    '2026-06-15',
    'Hôtel El Aurassi, Alger',
    'Sonatrach SPA',
    8,
    320000,
    60800,
    380800,
    'NON_REGLE',
    '',
    'virement',
    '',
    'commercial@kost.dz',
    "Acompte 50% à l'inscription",
  ],
  note: 'Statut ∈ REGLE / NON_REGLE. Si REGLE → opération encaissement créée + acompte/solde non générés.',
};

export const TEMPLATE_OPERATIONS: TemplateSpec = {
  filename: 'template_operations.xlsx',
  sheetName: 'Opérations',
  headers: [
    'Date opération',
    'Type',
    'Sous-type',
    'Tier',
    'Activité',
    'Service / Formation',
    'Motif',
    'Montant DZD',
    'Devise',
    'Mode paiement',
    'Compte bancaire',
    'Référence externe',
    'Justificatif URL',
    'Observation',
  ],
  example: [
    '2026-05-10',
    'sortie',
    'Honoraires formateur',
    'Mohamed Saadi',
    'Kost Academy',
    'Finance pour non-financiers',
    'Honoraires session FIN-001 du 15/06',
    120000,
    'DZD',
    'Banque',
    'CPA Hydra — Compte courant',
    'VIR-2026-0078',
    '',
    'Net après IRG 10%',
  ],
  note: 'Type ∈ entree/sortie · Devise ∈ DZD/EUR/USD · Compte = nom exact dans /parametres/banques.',
};

// Sprint 6.9.1 — Consolidé paiements formateurs B2B (14 colonnes)
export const TEMPLATE_CONSOLIDE_FORMATEURS: TemplateSpec = {
  filename: 'template_consolide_formateurs.xlsx',
  sheetName: 'Formateurs',
  headers: [
    'Année',
    'Période',
    'Formateur',
    'Coordonnées',
    'Formation',
    'Date de déroulement',
    'Montant (DA)',
    'Statut',
    'Date de règlement',
    'Mode paiement',
    'Référence opération',
    'Preuve paiement',
    'Commercial',
    'Remarques',
  ],
  example: [
    2026,
    'Mai 2026',
    'M. Saadoune Karim',
    'RIB CPA 00400000000000000102',
    'Leadership et management',
    '2026-05-12',
    180000,
    'PAYÉ',
    '2026-05-20',
    'Virement',
    'VIR-2026-0042',
    '',
    'Lila Boukhalfa',
    'Net après IRG 10%',
  ],
  note: 'Statut ∈ PAYÉ / NON PAYÉ / NON RÉTRIBUÉ · Mode ∈ Chèque / Virement / Espèces / CCP. Si PAYÉ → opération SORTIE créée à "Marquer payé" depuis l\'UI.',
  // Sprint 6.9.1.1 — auto-détection ligne d'en-tête. Les vrais Excel KOST ont une ligne 1
  // décorative ("KOST ACADEMY — Consolidé formateurs 2023 à 2026") qui décale les headers.
  requiredHeaders: ['Année', 'Formateur', 'Formation', 'Montant'],
};

// Sprint 6.9.4 — Caisse Trip Sun (immigration) — 26 colonnes
export const TEMPLATE_CAISSE_TRIP_SUN: TemplateSpec = {
  filename: 'template_caisse_trip_sun.csv',
  sheetName: 'Trip Sun',
  headers: [
    'Date',
    'N°',
    'catégorie',
    'REF',
    'NOM ET PRENOM',
    'H',
    'DISIGNATION',
    'RETOUR',
    'Recette service Evaluation preliminaire',
    'REC ACCOMP',
    'RECU CM',
    'Recette service',
    'Recette service visa Touristique',
    'DEPENSE',
    'BON',
    'SOLDE JOUR',
    'SOLDE FINAL',
    'Unnamed: 17',
    'nom de teleoperatrice',
    'nom de commercial',
    'DATE / N',
    'NUM TEL',
    'WILAYA',
    'FRAIS GOUV',
    'CCP / BANQ',
    'FRAIS GOUV2',
  ],
  example: [
    '2024-02-01',
    '1',
    'CCP',
    'CC-001',
    'Mlle Anonyme Test',
    '09:30',
    'EVALUATION PRELIMINAIRE',
    '',
    25000,
    '',
    '',
    '',
    '',
    '',
    '',
    25000,
    25000,
    '',
    'HIND',
    'Karim',
    '',
    '0555000000',
    'Alger',
    '',
    'CCP',
    '',
  ],
  note: 'CSV Trip Sun — 26 colonnes. Mapping designation/REF → type opération via regex (cf. useImportCaisse.ts).',
  requiredHeaders: ['Date', 'DISIGNATION', 'NOM ET PRENOM'],
};

// Sprint 6.9.4 — Caisse Formation B2C — 20 colonnes
export const TEMPLATE_CAISSE_B2C: TemplateSpec = {
  filename: 'template_caisse_b2c.csv',
  sheetName: 'B2C',
  headers: [
    'AL',
    'REF',
    'NOM PRENOM',
    'H',
    'DESIGNATION',
    'RETOUR',
    'RECETTE',
    'DEPENSE',
    'SOLDE JOUR',
    'RECETTE RESERVE',
    'N °BON',
    'CCP',
    'R AP',
    'DATE N',
    'NUM TEL',
    'WILAYA',
    'D/FOR',
    'F/FOR',
    'COM',
    'Unnamed: 19',
  ],
  example: [
    '1',
    'BON-001',
    'Stagiaire Test',
    '14:00',
    'PIZZALIO',
    '',
    18000,
    '',
    18000,
    '',
    'B-001',
    '',
    '',
    '2024-02-01',
    '0555000001',
    'Alger',
    '',
    '',
    'Imene',
    '',
  ],
  note: 'CSV Formation B2C — 20 colonnes. Mapping DESIGNATION/REF → type opération via regex (cf. useImportCaisse.ts).',
  requiredHeaders: ['NOM PRENOM', 'DESIGNATION', 'RECETTE'],
};

// Sprint 6.9.3 — Consolidé recouvrement entreprises B2B (16 colonnes)
export const TEMPLATE_RECOUVREMENT_ENTREPRISES: TemplateSpec = {
  filename: 'template_recouvrement_entreprises.xlsx',
  sheetName: 'Recouvrement',
  headers: [
    'Année',
    'Réf facture',
    'Formation',
    'Date',
    'Lieu',
    'Entreprise',
    'Participants',
    'HT (DA)',
    'TVA (DA)',
    'TTC (DA)',
    'Statut',
    'Date règlement',
    'Mode',
    'Référence virement',
    'Commerciale',
    'Observation',
  ],
  example: [
    2024,
    'FACT-2024-001',
    'HSE Niveau 1',
    '2024-01-15',
    'Hôtel Mercure Alger',
    'DP World Algérie',
    12,
    600000,
    114000,
    714000,
    'REGLE',
    '2024-01-25',
    'virement',
    'VIR-DP-001',
    'Amal',
    'Acompte 50% à la signature',
  ],
  note: 'Statut ∈ REGLE / NON_REGLE / PARTIEL · Mode ∈ Chèque / Virement / Espèces / CCP. Si REGLE → utilisez "Enregistrer encaissement" dans l\'UI pour générer l\'opération ENTRÉE Caisse.',
  requiredHeaders: ['Année', 'Réf facture', 'Entreprise', 'TTC'],
};

// Sprint 6.9.2 — Consolidé dettes fournisseurs B2B (15 colonnes)
export const TEMPLATE_DETTES_FOURNISSEURS: TemplateSpec = {
  filename: 'template_dettes_fournisseurs.xlsx',
  sheetName: 'Dettes Fournisseurs',
  headers: [
    'Fournisseur',
    'Téléphone',
    'N° compte (RIB)',
    'Email',
    'Année',
    'Formation',
    'Date formation',
    'N° facture',
    'Montant dette (DA)',
    'Montant réglé (DA)',
    'Statut',
    'Mode paiement',
    'Réf opération',
    'Date virement',
    'Observation',
  ],
  example: [
    'Hôtel El Aurassi',
    '021 74 82 52',
    'CPA — DZ59 0000 0000 0000 0000 0000 99',
    'reservation@elaurassi.dz',
    2024,
    'Formation HSE',
    '2024-01-15',
    'FAC-2024-0042',
    250000,
    0,
    'DETTE_NON_REGLEE',
    '',
    '',
    '',
    'Reste à payer — séjour participants',
  ],
  note: 'Statut ∈ DETTE_NON_REGLEE / REGLE / PARTIEL · Mode ∈ Chèque / Virement / Espèces / CCP. Si REGLE → utilisez "Enregistrer paiement" dans l\'UI pour générer l\'opération SORTIE Caisse.',
  requiredHeaders: ['Fournisseur', 'Année', 'N° facture'],
};
